import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0102 from "../lesson-02/lesson";
import lesson0103 from "../lesson-03/lesson";
import lesson0126 from "../lesson-26/lesson";
import lesson0127 from "../lesson-27/lesson";
const linkLesson0102 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0102.id);
const linkLesson0103 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0103.id);
const linkLesson0126 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0126.id);
const linkLesson0127 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0127.id);

const theory = ` <p>In this chapter we derive the equations for energy and power and define the acoustic intensity. Then we discuss the sources of damping for a plane wave.
<h3 class=sectionHead>Energy per unit volume</h3>
<h4 class=subsectionHead>reminder in mechanics</h4>
<p>
   In mechanics, the potential energy
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>E</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   is defined as the work
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>W</mi>
   </math>
   of forces necessary to bring the system from an arbitrary state (for example the rest position) to the actual state. The work
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>W</mi>
   </math>
   of a force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
   </math>
   is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>W</mi>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi class=MathClass-op>∫</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
         </mrow>
      </msubsup>
      <mi mathvariant=italic>Fdx</mi>
   </math>. In the case of a spring with a compliance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>C</mi>
   </math>
   the force required to extend a spring by an amount
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>C</mi>
   </math>. The work to return the spring to the rest condition (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>) is then
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>W</mi>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msubsup>
            <mfrac>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <mi>C</mi>
               </mrow>
            </mfrac>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>x</mi>
         </math>
      <td class=eq-no>(1)
</table>
<p>The potential energy of the spring is then
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>C</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   The kinetic energy
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>E</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   of a mass moving at a velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
   </math>
   is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>m</mi>
                  <msup>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   The total energy
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>E</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   is the sum of kinetic and potential energy. For a mass-spring system the total energy is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>C</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mi>m</mi>
                  <msup>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<h4 class=subsectionHead>application to a fluid</h4>
<p>
   An infinitesimal volume of fluid
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   is considered (this volume being infinitesimal, it would be more logical to note d
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>, but not to burden the notation we prefer to note it here
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>). Its kinetic energy is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>E</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>m</mi>
            <msup>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
   </math>
   and therefore the energy per unit volume is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>V</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   If this volume is inflated with a volume 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>dV</mi>
   </math>, it behave like a spring and its potential energy is then
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msubsup>
            <mi mathvariant=italic>Fdx</mi>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msubsup>
            <mfrac>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <mi mathvariant=italic>Sdx</mi>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msubsup>
            <mi mathvariant=italic>pdV</mi>
         </math>
      <td class=eq-no>(6)
</table>
<p>As
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mfrac>
               <mrow>
                  <mstyle class=text>
                     <mtext>d</mtext>
                  </mstyle>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>V</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mstyle class=text>
                     <mtext>d</mtext>
                  </mstyle>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mstyle class=text>
                     <mtext>d</mtext>
                  </mstyle>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mstyle class=text>
                     <mtext>d</mtext>
                  </mstyle>
                  <msup>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mstyle class=text>
               <mtext>and</mtext>
            </mstyle>
            <mi>p</mi>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(7)
</table>
<p>(cf. <a href="${linkLesson0102}" target="_blank">Lesson 1.2</a>), we have
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>V</mi>
                  <mstyle class=text>
                     <mtext>d</mtext>
                  </mstyle>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(8)
</table>
<p>then
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msubsup>
            <mi>p</mi>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msubsup>
            <mi>p</mi>
            <mfrac>
               <mrow>
                  <mi>V</mi>
                  <mstyle class=text>
                     <mtext>d</mtext>
                  </mstyle>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>V</mi>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>Finally, potential energy per unit volume is
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>V</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(10)
</table>
<p>Total energy is
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>V</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
<h4 class=subsectionHead>Case of a progressive wave</h4>
<p>
   For a progressive plane wave
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>
   (see <a href="${linkLesson0103}" target="_blank">Lesson 1.3</a>), then the potential energy and kinetic energy per unit volume are equal and given by
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin stretchy=false>∕</mo>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin stretchy=false>∕</mo>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   The energy density moves with the wave. For a sinusoidal progressive wave
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-rel>|</mo>
      <mi>A</mi>
      <mo class=MathClass-rel>|</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>ωt</mi>
      <mo class=MathClass-bin>±</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
         <mrow>
            <mi>A</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>. The potential energy and the kinetic energy per unit volume is given by
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>E</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>E</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <mi>A</mi>
                  <msup>
                     <mrow>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=italic>ωt</mi>
            <mo class=MathClass-bin>±</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mi>A</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(13)
</table>
<p>The time-averaged energy per unit volume, which in the case of a pure wave is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mover accent=false class=mml-overline>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-bin stretchy=false>∕</mo>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <msup>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(14)
</table>
<p>is often considered. The total energy is then given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mover accent=false class=mml-overline>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-bin stretchy=false>∕</mo>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <msup>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(15)
</table>
<p>
   In summary, for a progressive wave, the energy moves with the wave and the potential energy and kinetic energy at any point fluctuates in the same manner. For a sine wave the average energy is uniformly distributed (independent of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>).
<h4 class=subsectionHead>Case of a stationary wave</h4>
<p>For a stationary wave,
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mo class=MathClass-rel>|</mo>
            <mi>A</mi>
            <mo class=MathClass-rel>|</mo>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=italic>ωt</mi>
            <mo class=MathClass-bin>+</mo>
            <mover accent=false class=mml-overline>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(16)
      <tr>
         <td style="font-size: 9px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>v</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mo class=MathClass-rel>|</mo>
            <mi>A</mi>
            <mo class=MathClass-rel>|</mo>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=italic>ωt</mi>
            <mo class=MathClass-bin>+</mo>
            <mover accent=false class=mml-overline>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </math>
      <td class=eq-no>(17)
</table>
<p>Then,
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mo class=MathClass-rel>|</mo>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=italic>ωt</mi>
            <mo class=MathClass-bin>+</mo>
            <mover accent=false class=mml-overline>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            </math>
            <td class=eq-no>(18)
            <tr>
            <td style="font-size:8px">
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mo class=MathClass-rel>|</mo>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=italic>ωt</mi>
            <mo class=MathClass-bin>+</mo>
            <mover accent=false class=mml-overline>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(19)
</table>
<p>The kinetic and potential energies thus vary in quadrature (when one is zero, the other is maximum). The average energies are given by
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mover accent=false class=mml-overline>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-bin>∕</mo>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-rel>|</mo>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo> ( </mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo> ) </mo>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            </math>
            <td class=eq-no>(20)
            <tr>
            <td style="font-size: 10px">
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mover accent=false class=mml-overline>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-bin>∕</mo>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-rel>|</mo>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo>)</mo>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(21)
</table>
<p>The potential energy is maximum at the pressure antinodes and kinetic energy is maximum on the velocity antinodes. Note that the total energy is uniformly distributed as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mfrac>
               <mrow>
                  <mover accent=false class=mml-overline>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>E</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mi>V</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <mi>A</mi>
                  <msup>
                     <mrow>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(22)
</table>
<p>This is twice the energy of a progressive wave of the same amplitude, which should not be surprising since a standing wave is the sum of two progressive waves of same amplitude.
<h3 class=sectionHead>Acoustic intensity and power</h3>
<p>If we assume that there is no loss in the medium, there is conservation of acoustic energy (there is always energy conservation, but when there are losses, part of the energy is converted into heat). But when looking at the energy per unit volume we see that the total energy density fluctuates: this means that the energy moves. In order to highlight the transfer of acoustic power the concept of sound intensity is introduced.
<h4 class=subsectionHead>Energy conservation</h4>
<p>Consider the equations for the acoustic plane wave without loss
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable class=aligned>
                     <mtr>
                        <mtd columnalign=right>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂p</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                        <mtd columnalign=left>
                           <mo class=MathClass-rel>=</mo>
                           <mo class=MathClass-bin>−</mo>
                           <msub>
                              <mrow>
                                 <mi>ρ</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂v</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd columnalign=right>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂p</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                        <mtd columnalign=left>
                           <mo class=MathClass-rel>=</mo>
                           <mo class=MathClass-bin>−</mo>
                           <msub>
                              <mrow>
                                 <mi>ρ</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                           <msup>
                              <mrow>
                                 <mi>c</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msup>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂v</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(23)
</table>
<p>
   Multiplying the first equation (Euler equation) by the velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
   </math>, one obtains
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>v</mi>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>v</mi>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂v</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂t</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂pv</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <mi>p</mi>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂v</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂t</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
         </math>
      <td class=eq-no>(24)
</table>
<p>
   Substituting
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>∂v</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂x</mi>
         </mrow>
      </mfrac>
   </math>
   by its expression derived from the mass conservation law (second equation system above) the above equation becomes
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂pv</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mo class=MathClass-open>(</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <msup>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂t</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂pv</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <msub>
                     <mrow>
                        <mi>E</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>∕</mo>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂t</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
         </math>
      <td class=eq-no>(25)
</table>
<p>
   This equation expresses that the time fluctuations of the total energy (that is to say, the instantaneous power) at a given point are compensated by the spatial fluctuations of the product
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>pv</mi>
   </math>. This product
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>pv</mi>
   </math>
   is the acoustic intensity. It is important to notice that the intensity is a vector as velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
   </math>
   is itself a vector.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold-script>𝒫</mi>
      <mo class=MathClass-bin stretchy=false>∕</mo>
      <mi>V</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>∂</mi>
            <msub>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂t</mi>
         </mrow>
      </mfrac>
   </math>
   is the instantaneous power density and
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=bold-script>𝒫</mi>
            <mo class=MathClass-bin stretchy=false>∕</mo>
            <mi>V</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂I</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(26)
</table>
<p>
   This relationship expresses that the power coming out of a given volume results in a spatial intensity fluctuation. Indeed, if we consider a volume limited by two surfaces
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>, we have
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=bold-script>𝒫</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </msubsup>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂I</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
            <mi mathvariant=italic>dx</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(27)
</table>
<p>
   First term
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   is the power getting in the volume threw surface
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   the power getting out threw surface
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>. In general, power going through a surface appear to be the flux of the intensity
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=bold-script>𝒫</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mo>∬</mo>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </msub>
            <mi mathvariant=bold>I</mi>
            <mo class=MathClass-bin>⋅</mo>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi mathvariant=bold>S</mi>
         </math>
      <td class=eq-no>(28)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold>I</mi>
   </math>
   is the intensity vector and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi mathvariant=bold>S</mi>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>S</mi>
      <mi mathvariant=bold>n</mi>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold>n</mi>
   </math>
   unit vector normal to the surface (see <a href="#" data-img-src=${config.ASSETS_URL}/01/09/theory/figure-1.svg data-img-alt="Figure 1. Power passing through a surface is the flux of the intensity vector.">Figure 1</a>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/09/theory/figure-1.svg alt="Figure 1. Power passing through a surface is the flux of the intensity vector."><figcaption class=caption>Figure 1. Power passing through a surface is the flux of the intensity vector.</figcaption></figure>
<h4 class=subsectionHead>Acoustic intensity in harmonic regime</h4>
<p>
   Acoustic intensity is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mi>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>. In harmonic regime
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>ℜ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jωt</mi>
         </mrow>
      </msup>
      <mo class=MathClass-close>)</mo>
   </math>. Using the fact that for a complex number
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>z</mi>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ℜ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>z</mi>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>z</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∗</mo>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>z</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∗</mo>
         </mrow>
      </msup>
   </math>
   stands for the complex conjugate, we can write for the pressure as well as for the velocity
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∗</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close stretchy=false>)</mo>

            </math>
      <td class=eq-no>(29)
      <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>v</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>v</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∗</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(30)
</table>
<p>So intensity can be written
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>I</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-open>(</mo>
            <mi>p</mi>
            <msup>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∗</mo>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∗</mo>
               </mrow>
            </msup>
            <mi>v</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>pv</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∗</mo>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∗</mo>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>2</mn>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(31)
</table>
<p>The intensity can be separated into two terms: the mean intensity
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mover accent=false class=mml-overline>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>p</mi>
            <msup>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∗</mo>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∗</mo>
               </mrow>
            </msup>
            <mi>v</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(32)
</table>
<p>and the fluctuating intensity
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mover accent=false class=mml-overline>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mi>ℜ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant=italic>pv</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>jωt</mi>
               </mrow>
            </msup>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(33)
</table>
<p>The first term is the most important in practice because it is the one which indicates the transfer of energy from one area of space to another. The fluctuating term, sometimes called reactive intensity, describes periodic movements of the energy that does not reflect an overall transfer of energy from an area of the space to another.
<p>
   This is the mean intensity which is measured in practice when using an intensity probe. Note that the mean intensity is real as
   <span style="font-size: 13px">
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=false class=mml-overline>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>4</mn>
         </mrow>
      </mfrac>
      <mo class=MathClass-open>(</mo>
      <mi>p</mi>
      <msup>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∗</mo>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∗</mo>
         </mrow>
      </msup>
      <mi>v</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi>ℜ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>p</mi>
      <msup>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∗</mo>
         </mrow>
      </msup>
      <mo class=MathClass-close>)</mo>
   </math></span>.
<h4 class=subsectionHead>progressive and standing wave cases</h4>
<p>
   For a progressive wave, one has
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>±</mo>
            <mi mathvariant=italic>kx</mi>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>∓</mo>
      <mi>A</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>±</mo>
            <mi mathvariant=italic>kx</mi>
         </mrow>
      </msup>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
   </math>, then
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=false class=mml-overline>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mn>2</mn>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>. Intensity is uniform. For a stationary wave,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mo class=MathClass-rel>|</mo>
      <mi>A</mi>
      <mo class=MathClass-rel>|</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>j</mi>
      <mo class=MathClass-rel>|</mo>
      <mi>A</mi>
      <mo class=MathClass-rel>|</mo>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kx</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-close>)</mo>
   </math>. Pressure being real
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∗</mo>
         </mrow>
      </msup>
   </math>
   and the velocity being imaginary we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∗</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>v</mi>
   </math>, then the average intensity of a standing wave is zero at any point. This can be understood if one remembers that a standing wave is the sum of two progressive waves of same amplitude propagating in opposite directions: the intensities of the two waves are equal in modulus but opposite signs and their sum is zero.
<h4 class=subsectionHead>Reflection, transmission, absorption coefficients for intensity (or energy)</h4>
<p>
   In the same way that were defined reflection and transmission coefficients for pressure and velocity, it is possible to define the reflection and transmission coefficients for a change of medium. The intensity being the product of the pressure and velocity the reflection coefficient for intensity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mi>I</mi>
         </mrow>
      </msub>
   </math>
   is the product of the reflection coefficient for pressure and velocity which are opposite. So
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mi>I</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-rel>|</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(34)
</table>
<p>For a change of medium we obtain for both reflection and transmission coefficients for energy
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mi>I</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <mrow>
                     <mo fence=true form=prefix>|</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>Z</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>c</mi>
                                    <mn>1</mn>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-bin>−</mo>
                              <msub>
                                 <mrow>
                                    <mi>Z</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>c</mi>
                                    <mn>2</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>Z</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>c</mi>
                                    <mn>1</mn>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-bin>+</mo>
                              <msub>
                                 <mrow>
                                    <mi>Z</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>c</mi>
                                    <mn>2</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>|</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            </math>
            <td class=eq-no>(35)
            <tr>
            <td>
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>I</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>4</mn>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <msub>
                                 <mrow>
                                    <mi>Z</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>c</mi>
                                    <mn>1</mn>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-bin>+</mo>
                              <msub>
                                 <mrow>
                                    <mi>Z</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>c</mi>
                                    <mn>2</mn>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
         </math>
      <td class=eq-no>(36)
</table>
<p>
   We verify that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mi>I</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>τ</mi>
         </mrow>
         <mrow>
            <mi>I</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>, which expresses the energy conservation. Transmitted part of the energy is sometime considered as lost. Especially, in the context of absorbing materials (see <a href="${linkLesson0127}" target="_blank">Lesson 1.27</a>). So, the absorption of a wall is defined as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>α</mi>
               </mrow>
               <mrow>
                  <mi>I</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <mo class=MathClass-rel>|</mo>
            <mi>R</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(37)
</table>
<p>
   This may not be confused with the absorption coefficient of wave which is expressed in m<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msup>
   </math>
   (see <a href="${linkLesson0126}" target="_blank">Lesson 1.26</a>).
  <h3 class=sectionHead>References</h3>
  <dl class=thebibliography>
    <dt>[1]
    <dd>Leo J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
    <dt>[2]
    <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
    <dt>[3]
    <dd>Samuel Temkin. Elements of Acoustics. Wiley, New York, 1981.
  </dl>`;

export default theory;
